import React, { useRef, useState } from "react";
import "./SearchableCombobox.css";

export type SearchableComboboxOption = {
  value: string;
  label: string;
};

type SearchableComboboxProps = {
  id: string;
  label: string;
  value: string;
  selectedValue?: string;
  placeholder?: string;
  options: SearchableComboboxOption[];
  isInvalid?: boolean;
  warningText?: string;
  noOptionsText?: string;
  onInputChange: (value: string) => void;
  onSelect: (option: SearchableComboboxOption) => void;
  onClear: () => void;
  onBlur?: () => void;
};

export default function SearchableCombobox({
  id,
  label,
  value,
  selectedValue,
  placeholder,
  options,
  isInvalid = false,
  warningText,
  noOptionsText = "Nenhum item encontrado.",
  onInputChange,
  onSelect,
  onClear,
  onBlur
}: SearchableComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className="searchable-combobox">
      <label htmlFor={id}>{label}</label>

      <div className="searchable-combobox__input-wrapper">
        <input
          ref={inputRef}
          id={id}
          type="text"
          className={isInvalid ? "searchable-combobox__input is-invalid" : "searchable-combobox__input"}
          value={value}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            setTimeout(() => {
              setIsOpen(false);
              onBlur?.();
            }, 120);
          }}
          onChange={(e) => {
            onInputChange(e.target.value);
            setIsOpen(true);
          }}
          placeholder={placeholder}
          autoComplete="off"
        />

        {(value || selectedValue) && (
          <button
            type="button"
            className="searchable-combobox__clear"
            aria-label={`Limpar ${label.toLowerCase()}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onClear();
              setIsOpen(false);
              inputRef.current?.focus();
            }}
          >
            <svg
              className="searchable-combobox__clear-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>

      {isInvalid && warningText && (
        <small className="searchable-combobox__warning">{warningText}</small>
      )}

      {isOpen && (
        <div className="searchable-combobox__dropdown" role="listbox" aria-label={`Lista de ${label.toLowerCase()}`}>
          {options.length === 0 ? (
            <div className="searchable-combobox__empty">{noOptionsText}</div>
          ) : (
            options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`searchable-combobox__option ${selectedValue === option.value ? "active" : ""}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(option);
                  setIsOpen(false);
                }}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
